import { matchers } from 'millan';
import * as Monaco from 'monaco-editor/esm/vs/editor/editor.api';

const { PN_PREFIX, PN_LOCAL, PN_LOCAL_ESC, ECHAR } = matchers;

const keywords = [
  'a',
  'add',
  'all',
  'ask',
  'bind',
  'clear',
  'construct',
  'copymove',
  'create',
  'cyclic',
  'data',
  'default',
  'define',
  'delete',
  'describe',
  'drop',
  'end',
  'exists',
  'false',
  'from',
  'graph',
  'having',
  'insert',
  'length',
  'load',
  'minus',
  'named',
  'optional',
  'paths',
  'prefix',
  'select',
  'service',
  'shortest',
  'silent',
  'start',
  'to',
  'true',
  'union',
  'using',
  'values',
  'via',
  'where',
  'with',
];

const modifiers = [
  'limit',
  'offset',
  'reduced',
  'distinct',
  'group',
  'order',
  'by',
  'as',
  'filter',
];

const builtins = [
  'str',
  'count',
  'min',
  'max',
  'avg',
  'sample',
  'group_concat',
  'separator',
  'lang',
  'langmatches',
  'datatype',
  'bound',
  'iri',
  'uri',
  'bnode',
  'rand',
  'abs',
  'ceil',
  'floor',
  'round',
  'concat',
  'strlen',
  'ucase',
  'lcase',
  'encode_for_uri',
  'contains',
  'strstarts',
  'strends',
  'strbefore',
  'strafter',
  'year',
  'month',
  'day',
  'hours',
  'minutes',
  'seconds',
  'timezone',
  'tz',
  'now',
  'uuid',
  'struuid',
  'md5',
  'sha1',
  'sha256',
  'sha384',
  'sha512',
  'coalesce',
  'if',
  'strlang',
  'strdt',
  'sameterm',
  'isiri',
  'isuri',
  'isblank',
  'isliteral',
  'isnumeric',
];

const operators = [
  '|',
  '&',
  '=',
  '!',
  '<',
  '>',
  '/',
  'in',
  'not',
  '+',
  '-',
  '*',
  '%',
];

// https://microsoft.github.io/monaco-editor/monarch.html
export const sparqlMonarchTokensProvider: Monaco.languages.IMonarchLanguage = {
  defaultToken: 'invalid',
  ignoreCase: true,
  keywords,
  builtins,
  modifiers,
  operators,
  brackets: [
    { open: '(', close: ')', token: 'brackets.parenthesis' },
    { open: '{', close: '}', token: 'brackets.curly' },
    { open: '[', close: ']', token: 'brackets.square' },
  ],
  PN_PREFIX,
  PN_LOCAL_ESC,
  PN_LOCAL,
  ECHAR,
  // eslint-disable-next-line no-control-regex
  valid_iriref_chars: /[^<>\\{}|^`\u0000-\u0020]*/,
  tokenizer: {
    root: [
      { include: '@whitespace' },
      [/\[\s*\]/, 'identifier.iri.prefix.bnode'],
      [/[{}()[\]]/, '@brackets'],
      [/[;,.]/, 'delimiter'],
      [/_:/, 'identifier.iri.prefix.bnode', '@pn_local'], // bnode
      // [/\^{2}\w*:/, 'identifier.iri.prefix.type', '@pn_local'], // types
      [/\^{0,2}(@PN_PREFIX)?:/, 'identifier.iri.prefix', '@pn_local'], // prefixed IRIs
      [
        /(\^{0,2})(<)(@valid_iriref_chars)(>)/, // optional ending bracket to improve ux while typing
        [
          'identifier.iri.brackets',
          'identifier.iri.brackets',
          'identifier.iri',
          'identifier.iri.brackets',
        ],
      ],
      [/(\?|\$)(\w*)/, ['variable.prefix', 'variable']], // variables

      // strings
      // [/"[^"]*$/, 'string.invalid'],
      // [/'[^']*$/, 'string.invalid'],
      [/"/, 'string', '@string."'],
      [/'/, 'string', "@string.'"],
      [/(@)\w*/, 'identifier.langtag'], // langtag

      // operators
      [
        /[^\sa-zA-Z0-9]/,
        {
          cases: {
            '@operators': 'operators',
            '@default': 'invalid',
          },
        },
      ],

      // prefix declarations
      // [
      //   /(prefix\s+)([a-zA-z]*:\s*)(<)(@valid_iriref_chars)(>)/,
      //   [
      //     'keyword.prefix',
      //     'identifier.iri.prefix',
      //     'identifier.iri.brackets',
      //     'identifier.iri',
      //     'identifier.iri.brackets',
      //   ],
      // ],

      // keywords and builtins
      [
        /[a-zA-Z]\w*/,
        {
          cases: {
            '@keywords': {
              cases: {
                // can specify classes for specific keywords here.
                // for some reason a single string as the match in a cases
                // object ignores the case insensitivity setting. this can be solved
                // by wrapping in brackets or prepending with a tilda.
                'select|construct|update|ask|describe': 'keyword.querytype',
                where: 'keyword.optional',
                // ['prefix']: 'meta',
                '@default': 'keyword',
              },
            },
            '@builtins': {
              cases: {
                '@default': 'keyword.builtin',
              },
            },
            '@modifiers': 'keyword.modifier',
            '@operators': 'operators',
            '@default': 'invalid',
          },
        },
      ],

      [/[0-9]*\.?[0-9]*(eE[+-]?[0-9]+)?/, 'number'],
    ],
    string: [
      [/@ECHAR/, 'string.escape'], // escapes
      [/[^"']/, 'string'],
      [
        /["']/,
        {
          cases: {
            '$#==$S2': { token: 'string', next: '@pop' },
            '@default': 'string',
          },
        },
      ],
    ],
    pn_local: [
      [/@PN_LOCAL_ESC/, 'string.escape'], // escapes
      [/@PN_LOCAL/, 'identifier.iri', '@pop'],
      [/(?!@PN_LOCAL)/, 'identifier.iri', '@pop'],
    ],
    whitespace: [
      [/[\s\t\r\n]+/, 'white'],
      [/#.*$/, 'comment'],
    ],
  },
};
